import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loading } from "@/components/shared";
import { salaryLabel } from "@/pages/Jobs";
import { ArrowLeft, MapPin, Banknote, Building2, Briefcase, Clock, ExternalLink } from "lucide-react";

export default function JobDetail() {
  const { id } = useParams();
  const [job, setJob] = useState(null);

  useEffect(() => {
    api.get(`/jobs/${id}`).then(({ data }) => setJob(data)).catch(() => setJob(false));
    window.scrollTo(0, 0);
  }, [id]);

  if (job === null) return <Loading />;
  if (job === false) return <Loading label="Vacancy unavailable" />;

  return (
    <main className="mx-auto max-w-3xl px-5 py-10 lg:px-8">
      <Link to="/jobs" className="mb-6 inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-teal-400">
        <ArrowLeft className="h-4 w-4" /> All vacancies
      </Link>

      <div className="card-surface p-6 sm:p-8">
        <span className="rounded-full bg-[#E7F0EE] px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.08em] text-teal-600">{job.department}</span>
        <h1 className="mt-3 text-3xl font-bold text-ink">{job.title}</h1>
        <p className="mt-2 flex items-center gap-1.5 text-slate-400"><Building2 className="h-4 w-4" /> {job.company_name}</p>

        <div className="mt-6 flex flex-wrap gap-3">
          <span className="inline-flex items-center gap-2 rounded-full border border-teal-400/30 bg-teal-500/5 px-4 py-2 text-sm font-semibold text-teal-600"><Banknote className="h-4 w-4" /> {salaryLabel(job)}</span>
          <span className="inline-flex items-center gap-2 rounded-full border border-[#DDDAD1] px-4 py-2 text-sm text-slate-300"><MapPin className="h-4 w-4 text-teal-400" /> {job.market}</span>
          <span className="inline-flex items-center gap-2 rounded-full border border-[#DDDAD1] px-4 py-2 text-sm text-slate-300"><Briefcase className="h-4 w-4 text-teal-400" /> {job.working_model}</span>
        </div>

        <div className="mt-8 border-t border-[#DDDAD1] pt-6">
          <h2 className="text-lg font-bold text-ink">About the role</h2>
          <p className="mt-3 whitespace-pre-line break-words leading-relaxed text-slate-300">{job.description}</p>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          {job.apply_url ? (
            <a href={job.apply_url} target="_blank" rel="noreferrer" data-testid="job-apply" className="btn-teal inline-flex items-center gap-1.5 text-sm">Apply via the employer <ExternalLink className="h-3.5 w-3.5" /></a>
          ) : (
            <span data-testid="job-apply" className="cursor-default rounded-[8px] border border-[#DDDAD1] px-6 py-3 text-sm text-slate-500">No application link provided</span>
          )}
          {job.company_id && (
            <Link to={`/company/${job.company_id}`} className="inline-flex items-center gap-2 rounded-[8px] border border-[#DDDAD1] px-6 py-3 text-sm text-slate-200 transition-colors hover:border-teal-400/50">
              Read reviews of {job.company_name}
            </Link>
          )}
        </div>
        <p className="mt-4 flex items-center gap-1.5 text-xs text-slate-500"><Clock className="h-3.5 w-3.5" /> Posted {new Date(job.posted_at).toLocaleDateString()}</p>
      </div>
    </main>
  );
}
